import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { adminAPI, ciudadesAPI, estadosAPI } from "../../api";
import { TIPOS_PROPIEDAD, AMENIDADES, formatFecha } from "../../utils";
import FotosOrdenables from "../../components/admin/FotosOrdenables";
import toast from "react-hot-toast";
import "./PropiedadForm.css";

// Mismo orden que en el portal: portada primero, luego por sort_order
const ordenarFotos = (imgs = []) =>
    [...imgs].sort((a, b) =>
        (b.is_cover === true) - (a.is_cover === true) ||
        (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
        String(a.id).localeCompare(String(b.id))
    );

const TABS = [
  { id: "general", label: "1. General" },
  { id: "precio", label: "2. Precio" },
  { id: "ubicacion", label: "3. Ubicación" },
  { id: "caracteristicas", label: "4. Características" },
  { id: "amenidades", label: "5. Amenidades" },
  { id: "descripcion", label: "6. Descripción" },
  { id: "fotos", label: "7. Fotos" },
];

const INITIAL = {
  title: "", custom_id: "", slug: "",
  operation: "VENTA", type: "CASA", status: "ACTIVA",
  featured: false, internal_notes: "",
  price: "", currency: "MXN", price_negotiable: false, show_price: true,
  address: "", neighborhood: "", city: "Córdoba", state: "Veracruz", zip_code: "",
  latitude: "", longitude: "", map_zoom: 15,
  bedrooms: "", bathrooms: "", half_bathrooms: "", parking_spaces: "",
  construction_m2: "", land_m2: "", floors: "", year_built: "", furnished: "",
  has_cistern: false, has_water_tank: false, has_natural_gas: false,
  has_electricity: true, has_water: true, has_drainage: true, has_internet: false,
  amenities: [], description: "", seo_title: "", seo_description: "",
};

export default function PropiedadForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [tab, setTab] = useState("general");
  const [form, setForm] = useState(INITIAL);
  const [imagenes, setImagenes] = useState([]);
  const [archivosNuevos, setArchivosNuevos] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [cargando, setCargando] = useState(isEdit);
  const [subiendo, setSubiendo] = useState(false);
  const [guardandoOrden, setGuardandoOrden] = useState(false);
  const [ciudades, setCiudades] = useState([]);
  const [estados, setEstados] = useState([]);
  const fileInputRef = useRef();

  useEffect(() => {
    ciudadesAPI.listar()
        .then((r) => setCiudades(r.data.data.map((c) => c.nombre)))
        .catch(() => {});
    estadosAPI.listar()
        .then((r) => setEstados(r.data.data.map((e) => e.nombre)))
        .catch(() => {});
  }, []);

  useEffect(() => {
    if (isEdit) {
      adminAPI.detalle(id)
          .then((r) => {
            const p = r.data.data;
            setForm({
              ...INITIAL, ...p,
              price: p.price || "", bedrooms: p.bedrooms ?? "", bathrooms: p.bathrooms ?? "",
              half_bathrooms: p.half_bathrooms ?? "", parking_spaces: p.parking_spaces ?? "",
              construction_m2: p.construction_m2 ?? "", land_m2: p.land_m2 ?? "",
              floors: p.floors ?? "", year_built: p.year_built ?? "",
              latitude: p.latitude ?? "", longitude: p.longitude ?? "",
              amenities: p.amenities || [],
            });
            setImagenes(ordenarFotos(p.images || []));
          })
          .catch(() => toast.error("Error cargando propiedad"))
          .finally(() => setCargando(false));
    }
  }, [id, isEdit]);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  const setNum = (key, val) => set(key, val === "" ? "" : val);

  const toggleAmenidad = (val) => {
    setForm((f) => ({
      ...f,
      amenities: f.amenities.includes(val)
          ? f.amenities.filter((a) => a !== val)
          : [...f.amenities, val],
    }));
  };

  const handleGuardar = async (publicar = false) => {
    if (!form.title) { toast.error("El título es requerido"); setTab("general"); return; }
    if (!form.city) { toast.error("La ciudad es requerida"); setTab("ubicacion"); return; }
    if (!form.operation) { toast.error("La operación es requerida"); setTab("general"); return; }
    if (!form.type) { toast.error("El tipo de propiedad es requerido"); setTab("general"); return; }

    setGuardando(true);
    const payload = {
      ...form,
      status: publicar ? "ACTIVA" : form.status,
      price: form.price ? parseFloat(form.price) : null,
      bedrooms: form.bedrooms !== "" ? parseInt(form.bedrooms) : null,
      bathrooms: form.bathrooms !== "" ? parseFloat(form.bathrooms) : null,
      half_bathrooms: form.half_bathrooms !== "" ? parseInt(form.half_bathrooms) : null,
      parking_spaces: form.parking_spaces !== "" ? parseInt(form.parking_spaces) : null,
      construction_m2: form.construction_m2 !== "" ? parseFloat(form.construction_m2) : null,
      land_m2: form.land_m2 !== "" ? parseFloat(form.land_m2) : null,
      floors: form.floors !== "" ? parseInt(form.floors) : null,
      year_built: form.year_built !== "" ? parseInt(form.year_built) : null,
      latitude: form.latitude !== "" ? parseFloat(form.latitude) : null,
      longitude: form.longitude !== "" ? parseFloat(form.longitude) : null,
    };

    try {
      let propId = id;
      if (isEdit) {
        await adminAPI.actualizar(id, payload);
      } else {
        const res = await adminAPI.crear(payload);
        propId = res.data.data.id;
      }

      // Subir imágenes nuevas si las hay
      if (archivosNuevos.length > 0) {
        await subirImagenes(propId, archivosNuevos);
      }

      toast.success(isEdit ? "Propiedad actualizada" : "Propiedad creada");
      navigate("/admin/properties");
    } catch (err) {
      toast.error(err.response?.data?.message || "Error guardando");
      if (err.response?.data?.campo === "custom_id") setTab("general");
    } finally {
      setGuardando(false);
    }
  };

  // "Publicar": pone el Status en Activa (se ve en el campo) y guarda.
  // Si estaba Reservada, Vendida o Rentada, primero pregunta para no reactivarla por accidente.
  const handlePublicar = () => {
    const etiquetas = { RESERVADA: "Reservada", VENDIDA: "Vendida", RENTADA: "Rentada" };
    if (etiquetas[form.status]) {
      const ok = window.confirm(
          `Esta propiedad está marcada como ${etiquetas[form.status]}. ¿Quieres publicarla como Activa?`
      );
      if (!ok) return;
    }
    set("status", "ACTIVA");
    handleGuardar(true);
  };

  const subirImagenes = async (propId, files) => {
    setSubiendo(true);
    const formData = new FormData();
    files.forEach((f) => formData.append("images", f));
    try {
      const res = await adminAPI.subirImagenes(propId, formData);
      setImagenes((prev) => [...prev, ...res.data.data]);
      setArchivosNuevos([]);
      setPreviews([]);
    } catch (err) {
      toast.error("Error subiendo imágenes");
    } finally {
      setSubiendo(false);
    }
  };

  const handleSeleccionarArchivos = (files) => {
    const validos = Array.from(files).filter((f) =>
        ["image/jpeg", "image/png", "image/webp"].includes(f.type)
    );
    setArchivosNuevos((prev) => [...prev, ...validos]);
    const nuevasPreviews = validos.map((f) => URL.createObjectURL(f));
    setPreviews((prev) => [...prev, ...nuevasPreviews]);
  };

  const quitarPreview = (idx) => {
    setArchivosNuevos((p) => p.filter((_, i) => i !== idx));
    setPreviews((p) => p.filter((_, i) => i !== idx));
  };

  // Guarda el nuevo orden. La foto #1 queda como portada.
  const reordenarFotos = async (nuevas) => {
    const antes = imagenes;
    const conPortada = nuevas.map((img, i) => ({ ...img, is_cover: i === 0, sort_order: i }));
    setImagenes(conPortada);
    setGuardandoOrden(true);
    try {
      await adminAPI.reordenarImagenes(id, conPortada.map((i) => i.id));
      toast.success(
          conPortada[0]?.id !== antes[0]?.id ? "Orden guardado · nueva portada" : "Orden guardado"
      );
    } catch (err) {
      setImagenes(antes); // regresar las fotos a como estaban
      toast.error(err.response?.data?.message || "No se pudo guardar el orden");
    } finally {
      setGuardandoOrden(false);
    }
  };

  const eliminarImagen = async (imgId) => {
    if (!window.confirm("¿Eliminar esta foto? No se puede deshacer.")) return;
    try {
      await adminAPI.eliminarImagen(id, imgId);
      // Si se borró la #1, la siguiente pasa a ser portada (igual que en el backend)
      setImagenes((imgs) =>
          imgs.filter((i) => i.id !== imgId).map((img, i) => ({ ...img, is_cover: i === 0 }))
      );
      toast.success("Imagen eliminada");
    } catch { toast.error("Error eliminando imagen"); }
  };

  if (cargando) return <div className="spinner" />;

  return (
      <div className="prop-form-page fade-in">
        <div className="page-header">
          <div>
            <h1 className="heading-serif page-title">
              {isEdit ? "Editar propiedad" : "Nueva propiedad"}
            </h1>
            {isEdit && (
                <div className="pf-meta">
                  <span>
                    {form.created_by_name
                        ? <>Creada por <b>{form.created_by_name}</b> (@{form.created_by_username})</>
                        : "Creada por: sin registro"}
                    {form.created_at && <> · {formatFecha(form.created_at)}</>}
                  </span>
                  {form.slug && (
                      <a href={`/propiedades/${form.slug}`} target="_blank" rel="noreferrer"
                         className="btn btn-ghost btn-sm">
                        👁️ Ver en portal
                      </a>
                  )}
                </div>
            )}
          </div>
          <Link to="/admin/properties" className="btn btn-ghost">← Volver</Link>
        </div>

        {/* Tabs */}
        <div className="tabs" style={{ marginBottom: "1.5rem", overflowX: "auto" }}>
          {TABS.map((t) => (
              <button key={t.id} className={`tab-btn ${tab === t.id ? "active" : ""}`}
                      onClick={() => setTab(t.id)}>
                {t.label}
              </button>
          ))}
        </div>

        <div className="prop-form-body">
          {/* ── TAB 1: GENERAL ── */}
          {tab === "general" && (
              <div className="form-tab card">
                <div className="form-grid-2">
                  <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                    <label className="form-label">Título de la propiedad *</label>
                    <input className="form-input" value={form.title} maxLength={120}
                           onChange={(e) => set("title", e.target.value)}
                           placeholder="Ej: Casa en Colonia Jardines, Córdoba" />
                    <span className="form-hint">{form.title.length}/120 caracteres</span>
                  </div>
                  <div className="form-group">
                    <label className="form-label">ID / Clave personalizada</label>
                    <input className="form-input" value={form.custom_id || ""}
                           onChange={(e) => set("custom_id", e.target.value)}
                           placeholder="Ej: CASA-CBA-001" />
                    <span className="form-hint">Identificador interno de tu elección</span>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select className="form-select" value={form.status} onChange={(e) => set("status", e.target.value)}>
                      <option value="ACTIVA">✅ Activa</option>
                      <option value="INACTIVA">⛔ Inactiva</option>
                      <option value="RESERVADA">🔒 Reservada</option>
                      <option value="VENDIDA">💰 Vendida</option>
                      <option value="RENTADA">🔑 Rentada</option>
                    </select>
                  </div>
                </div>

                <div className="form-section-title">Operación *</div>
                <div className="op-big-btns">
                  {[["VENTA", "🏷️", "Venta"], ["RENTA", "🔑", "Renta"]].map(([val, icon, label]) => (
                      <button key={val} type="button"
                              className={`op-big-btn ${form.operation === val ? "active" : ""}`}
                              onClick={() => set("operation", val)}>
                        <span style={{ fontSize: "1.5rem" }}>{icon}</span>
                        <span>{label}</span>
                      </button>
                  ))}
                </div>

                <div className="form-section-title">Tipo de inmueble *</div>
                <div className="tipo-grid-btns">
                  {TIPOS_PROPIEDAD.map((t) => (
                      <button key={t.value} type="button"
                              className={`tipo-btn ${form.type === t.value ? "active" : ""}`}
                              onClick={() => set("type", t.value)}>
                        <span>{t.emoji}</span>
                        <span>{t.label}</span>
                      </button>
                  ))}
                </div>

                <div className="form-section-title">Configuración</div>
                <div className="toggle-row">
                  <div className="toggle-wrapper">
                    <button type="button" className={`toggle ${form.featured ? "on" : ""}`} onClick={() => set("featured", !form.featured)} />
                    <span>⭐ Propiedad destacada <small>(aparece en el inicio del portal)</small></span>
                  </div>
                </div>

                <div className="form-group" style={{ marginTop: "1rem" }}>
                  <label className="form-label private">🔒 Notas internas (solo visible para ti, nunca en el portal)</label>
                  <textarea className="form-textarea" rows={3} value={form.internal_notes || ""}
                            onChange={(e) => set("internal_notes", e.target.value)}
                            placeholder="Contacto del propietario, observaciones, recordatorios..." />
                </div>
              </div>
          )}

          {/* ── TAB 2: PRECIO ── */}
          {tab === "precio" && (
              <div className="form-tab card">
                <div className="form-grid-2">
                  <div className="form-group">
                    <label className="form-label">Precio</label>
                    <input type="number" className="form-input" value={form.price}
                           onChange={(e) => setNum("price", e.target.value)}
                           placeholder="0.00" min="0" step="1000" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Moneda</label>
                    <div className="op-big-btns" style={{ gap: ".75rem" }}>
                      {[["MXN", "🇲🇽"], ["USD", "🇺🇸"]].map(([val, flag]) => (
                          <button key={val} type="button"
                                  className={`op-big-btn ${form.currency === val ? "active" : ""}`}
                                  style={{ padding: ".75rem 1.5rem" }}
                                  onClick={() => set("currency", val)}>
                            <span>{flag}</span><span>{val}</span>
                          </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="toggle-row">
                  <div className="toggle-wrapper">
                    <button type="button" className={`toggle ${form.price_negotiable ? "on" : ""}`} onClick={() => set("price_negotiable", !form.price_negotiable)} />
                    <span>Precio a tratar / negociable</span>
                  </div>
                  <div className="toggle-wrapper">
                    <button type="button" className={`toggle ${form.show_price ? "on" : ""}`} onClick={() => set("show_price", !form.show_price)} />
                    <span>Mostrar precio en el portal {!form.show_price && <span style={{ color: "var(--color-text-muted)", fontSize: ".8rem" }}>(se mostrará "Consultar precio")</span>}</span>
                  </div>
                </div>
                {form.price && (
                    <div className="precio-preview card" style={{ background: "var(--color-bg-secondary)", padding: "1.25rem", marginTop: "1rem" }}>
                      <p style={{ fontSize: ".8rem", color: "var(--color-text-muted)", marginBottom: ".5rem" }}>Preview en el portal:</p>
                      <p style={{ fontFamily: "var(--font-heading)", fontSize: "1.75rem", color: "var(--color-primary)", fontWeight: 700 }}>
                        {form.show_price
                            ? new Intl.NumberFormat("es-MX", { style: "currency", currency: form.currency, maximumFractionDigits: 0 }).format(form.price)
                            : "Consultar precio"}
                      </p>
                      {form.price_negotiable && form.show_price && <p style={{ fontSize: ".85rem", color: "var(--color-text-muted)" }}>Precio a tratar</p>}
                    </div>
                )}
              </div>
          )}

          {/* ── TAB 3: UBICACIÓN ── */}
          {tab === "ubicacion" && (
              <div className="form-tab card">
                <div className="form-grid-2">
                  <div className="form-group">
                    <label className="form-label">Calle y número</label>
                    <input className="form-input" value={form.address || ""} onChange={(e) => set("address", e.target.value)} placeholder="Av. Principal #123" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Colonia / Fraccionamiento</label>
                    <input className="form-input" value={form.neighborhood || ""} onChange={(e) => set("neighborhood", e.target.value)} placeholder="Col. Jardines" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Ciudad *</label>
                    <select className="form-select" value={form.city} onChange={(e) => set("city", e.target.value)}>
                      {ciudades.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Estado</label>
                    <select className="form-select" value={form.state} onChange={(e) => set("state", e.target.value)}>
                      {estados.map((e) => <option key={e} value={e}>{e}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Código postal</label>
                    <input className="form-input" value={form.zip_code || ""} onChange={(e) => set("zip_code", e.target.value)} placeholder="94000" maxLength={10} />
                  </div>
                </div>

                <div className="form-section-title">Geolocalización</div>
                <div className="form-group">
                  <label className="form-label">Coordenadas</label>
                  <input
                      className="form-input"
                      placeholder="Pega aquí: 18.886840, -96.938591"
                      value={form.latitude && form.longitude ? `${form.latitude}, ${form.longitude}` : ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "") {
                          set("latitude", "");
                          set("longitude", "");
                          return;
                        }
                        const partes = val.split(",").map((p) => p.trim());
                        if (partes.length === 2) {
                          const lat = parseFloat(partes[0]);
                          const lng = parseFloat(partes[1]);
                          if (!isNaN(lat) && !isNaN(lng)) {
                            set("latitude", lat);
                            set("longitude", lng);
                          }
                        }
                      }}
                  />
                  <span className="form-hint">
        💡 En Google Maps: clic derecho sobre el punto → copia las coordenadas → pégalas aquí
      </span>
                </div>
                <div className="form-group" style={{ marginTop: ".75rem" }}>
                  <label className="form-label">Zoom del mapa (1-20)</label>
                  <input type="range" min="10" max="20" value={form.map_zoom || 15}
                         onChange={(e) => set("map_zoom", parseInt(e.target.value))} />
                  <span className="form-hint">Zoom actual: {form.map_zoom || 15}</span>
                </div>
              </div>
          )}

          {/* ── TAB 4: CARACTERÍSTICAS ── */}
          {tab === "caracteristicas" && (
              <div className="form-tab card">
                <div className="form-section-title">Espacios</div>
                <div className="form-grid-3">
                  {[
                    ["bedrooms", "Recámaras", "🛏"], ["bathrooms", "Baños", "🚿"],
                    ["half_bathrooms", "Medios baños", "🚽"], ["parking_spaces", "Estacionamiento", "🚗"],
                    ["floors", "Pisos", "🏢"],
                  ].map(([key, label, icon]) => (
                      <div key={key} className="form-group">
                        <label className="form-label">{icon} {label}</label>
                        <input type="number" min="0" step={key === "bathrooms" ? "0.5" : "1"}
                               className="form-input" value={form[key] || ""}
                               onChange={(e) => setNum(key, e.target.value)} placeholder="—" />
                      </div>
                  ))}
                </div>

                <div className="form-section-title">Superficies</div>
                <div className="form-grid-3">
                  {[
                    ["construction_m2", "M² de construcción", "📐"],
                    ["land_m2", "M² de terreno", "🌿"],
                  ].map(([key, label, icon]) => (
                      <div key={key} className="form-group">
                        <label className="form-label">{icon} {label}</label>
                        <input type="number" min="0" step="0.01" className="form-input"
                               value={form[key] || ""} onChange={(e) => setNum(key, e.target.value)} placeholder="—" />
                      </div>
                  ))}
                  <div className="form-group">
                    <label className="form-label">📅 Año de construcción</label>
                    <input type="number" min="1900" max={new Date().getFullYear()} className="form-input"
                           value={form.year_built || ""} onChange={(e) => setNum("year_built", e.target.value)} placeholder="Ej: 2005" />
                  </div>
                </div>

                <div className="form-section-title">Estado del inmueble</div>
                <div className="form-group">
                  <label className="form-label">🛋️ Amueblado</label>
                  <select className="form-select" value={form.furnished || ""} onChange={(e) => set("furnished", e.target.value)}>
                    <option value="">Sin especificar</option>
                    <option value="SIN_AMUEBLAR">Sin amueblar</option>
                    <option value="SEMI_AMUEBLADO">Semi-amueblado</option>
                    <option value="AMUEBLADO">Totalmente amueblado</option>
                  </select>
                </div>

                <div className="form-section-title">Servicios disponibles</div>
                <div className="servicios-checks">
                  {[
                    ["has_cistern", "💧 Cisterna"], ["has_water_tank", "🪣 Tinaco"],
                    ["has_natural_gas", "🔥 Gas natural"], ["has_electricity", "⚡ Electricidad CFE"],
                    ["has_water", "🚰 Agua municipal"], ["has_drainage", "🪠 Drenaje"],
                    ["has_internet", "📡 Internet/Fibra óptica"],
                  ].map(([key, label]) => (
                      <label key={key} className="check-row">
                        <input type="checkbox" checked={form[key] || false} onChange={(e) => set(key, e.target.checked)} />
                        {label}
                      </label>
                  ))}
                </div>
              </div>
          )}

          {/* ── TAB 5: AMENIDADES ── */}
          {tab === "amenidades" && (
              <div className="form-tab card">
                <p className="form-hint" style={{ marginBottom: "1.5rem" }}>
                  Selecciona todas las amenidades con las que cuenta la propiedad.
                </p>
                <div className="amenidades-checks">
                  {AMENIDADES.map((a) => (
                      <label key={a.value} className={`amenidad-check ${form.amenities.includes(a.value) ? "checked" : ""}`}>
                        <input type="checkbox" checked={form.amenities.includes(a.value)}
                               onChange={() => toggleAmenidad(a.value)} />
                        <span>{a.emoji}</span>
                        <span>{a.label}</span>
                      </label>
                  ))}
                </div>
                {form.amenities.length > 0 && (
                    <p className="form-hint" style={{ marginTop: "1rem" }}>
                      ✅ {form.amenities.length} amenidad{form.amenities.length !== 1 ? "es" : ""} seleccionada{form.amenities.length !== 1 ? "s" : ""}
                    </p>
                )}
              </div>
          )}

          {/* ── TAB 6: DESCRIPCIÓN ── */}
          {tab === "descripcion" && (
              <div className="form-tab card">
                <div className="form-group">
                  <label className="form-label">Descripción pública</label>
                  <textarea className="form-textarea" rows={10} value={form.description || ""}
                            onChange={(e) => set("description", e.target.value)}
                            placeholder="Escribe aquí la descripción de la propiedad que verán los clientes en el portal..." />
                  <span className="form-hint">{(form.description || "").length} caracteres</span>
                </div>

                <div className="form-section-title" style={{ marginTop: "1.5rem" }}>
                  SEO (opcional)
                  <span style={{ fontSize: ".8rem", fontWeight: 400, color: "var(--color-text-muted)", marginLeft: ".5rem" }}>
                Mejora el posicionamiento en Google
              </span>
                </div>
                <div className="form-group">
                  <label className="form-label">Título SEO <span style={{ fontWeight: 400 }}>(max 60 chars)</span></label>
                  <input className="form-input" value={form.seo_title || ""} maxLength={70}
                         onChange={(e) => set("seo_title", e.target.value)}
                         placeholder={form.title || "Título SEO personalizado"} />
                  <div className="seo-bar">
                    <div className="seo-bar-fill" style={{
                      width: `${Math.min(100, ((form.seo_title?.length || 0) / 60) * 100)}%`,
                      background: (form.seo_title?.length || 0) > 60 ? "var(--color-error)" : (form.seo_title?.length || 0) > 40 ? "var(--color-success)" : "var(--color-warning)",
                    }} />
                  </div>
                  <span className="form-hint">{(form.seo_title || "").length}/60</span>
                </div>
                <div className="form-group">
                  <label className="form-label">Descripción SEO <span style={{ fontWeight: 400 }}>(max 160 chars)</span></label>
                  <textarea className="form-textarea" rows={3} value={form.seo_description || ""} maxLength={170}
                            onChange={(e) => set("seo_description", e.target.value)}
                            placeholder="Descripción breve para los resultados de Google..." />
                  <span className="form-hint">{(form.seo_description || "").length}/160</span>
                </div>

                {/* Preview Google */}
                {(form.seo_title || form.title) && (
                    <div className="google-preview">
                      <p className="google-preview-label">Preview en Google:</p>
                      <div className="google-preview-box">
                        <p className="gp-url">tuinmobiliaria.com › propiedades › {form.slug || "..."}</p>
                        <p className="gp-title">{form.seo_title || form.title}</p>
                        <p className="gp-desc">{form.seo_description || (form.description?.replace(/<[^>]*>/g, "").slice(0, 160)) || "Sin descripción SEO"}</p>
                      </div>
                    </div>
                )}
              </div>
          )}

          {/* ── TAB 7: FOTOS ── */}
          {tab === "fotos" && (
              <div className="form-tab card">
                {!isEdit && (
                    <div className="fotos-notice">
                      ⚠️ Primero guarda la propiedad, luego podrás subir fotos. Al crear la propiedad con fotos seleccionadas aquí, se subirán automáticamente.
                    </div>
                )}

                {/* Dropzone */}
                <div
                    className="dropzone"
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault(); handleSeleccionarArchivos(e.dataTransfer.files); }}
                >
                  <div className="dropzone-icon">📷</div>
                  <p style={{ fontWeight: 600, color: "var(--color-primary)" }}>Arrastra fotos aquí o haz click para seleccionar</p>
                  <p style={{ fontSize: ".85rem", color: "var(--color-text-muted)", marginTop: ".25rem" }}>
                    JPG, PNG o WEBP · Máximo 8MB por foto · Hasta 25 fotos
                  </p>
                  <p style={{ fontSize: ".8rem", color: "var(--color-text-muted)", marginTop: ".25rem" }}>
                    {imagenes.length + archivosNuevos.length} / 25 fotos
                  </p>
                </div>
                <input ref={fileInputRef} type="file" multiple accept="image/jpeg,image/png,image/webp"
                       style={{ display: "none" }} onChange={(e) => handleSeleccionarArchivos(e.target.files)} />

                {/* Previews de nuevas */}
                {previews.length > 0 && (
                    <div style={{ marginTop: "1.5rem" }}>
                      <p style={{ fontWeight: 600, marginBottom: ".75rem" }}>Nuevas fotos a subir ({previews.length})</p>
                      <div className="fotos-grid">
                        {previews.map((src, i) => (
                            <div key={i} className="foto-item">
                              <img src={src} alt={`preview ${i}`} />
                              <div className="foto-overlay">
                                <button className="foto-btn-del" onClick={() => quitarPreview(i)}>✕</button>
                              </div>
                            </div>
                        ))}
                      </div>
                      {isEdit && (
                          <button className="btn btn-primary" style={{ marginTop: "1rem" }}
                                  disabled={subiendo} onClick={() => subirImagenes(id, archivosNuevos)}>
                            {subiendo ? "Subiendo..." : `📤 Subir ${archivosNuevos.length} foto${archivosNuevos.length !== 1 ? "s" : ""}`}
                          </button>
                      )}
                    </div>
                )}

                {/* Fotos guardadas (se pueden reordenar arrastrando) */}
                {isEdit && imagenes.length > 0 && (
                    <div style={{ marginTop: "1.5rem" }}>
                      <p style={{ fontWeight: 600, marginBottom: ".25rem" }}>
                        Fotos guardadas ({imagenes.length})
                        {guardandoOrden && (
                            <span style={{ fontWeight: 400, fontSize: ".8rem", color: "var(--color-text-muted)", marginLeft: ".5rem" }}>
                              Guardando orden...
                            </span>
                        )}
                      </p>
                      <p className="fo-ayuda">
                        Arrastra las fotos para cambiar el orden (en celular, mantén presionada la foto y luego muévela).
                        La foto <b>#1</b> es la portada y así aparecerán en el sitio.
                      </p>
                      <FotosOrdenables
                          imagenes={imagenes}
                          onReordenar={reordenarFotos}
                          onEliminar={eliminarImagen}
                          deshabilitado={guardandoOrden || subiendo}
                      />
                    </div>
                )}
              </div>
          )}
        </div>

        {/* Barra de acciones fija */}
        <div className="form-actions-bar">
          <Link to="/admin/properties" className="btn btn-ghost">Cancelar</Link>
          <div style={{ display: "flex", gap: ".75rem" }}>
            {/* Guarda exactamente el Status elegido */}
            <button className={`btn ${form.status === "ACTIVA" ? "btn-accent btn-lg" : "btn-outline"}`}
                    onClick={() => handleGuardar(false)} disabled={guardando}
                    title="Guarda la propiedad con el Status que elegiste">
              {guardando ? "Guardando..." : "💾 Guardar"}
            </button>
            {/* Si ya está Activa, "Publicar" haría lo mismo que "Guardar": no se muestra */}
            {form.status !== "ACTIVA" && (
                <button className="btn btn-accent btn-lg" onClick={handlePublicar} disabled={guardando}
                        title="Cambia el Status a Activa y la publica en el portal">
                  {guardando ? "Guardando..." : "✅ Publicar"}
                </button>
            )}
          </div>
        </div>
      </div>
  );
}